import { Moment } from 'moment';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';
import { getEntitiesData, postDownloadEntitesData } from 'src/apis/QuerybuilderAPI';
import { INVOICE_PAYMENT_STATUS, INVOICE_STATUS } from 'src/constants/Invoices';
import { SEARCH_OPERATORS_PRIMITIVE } from 'src/constants/SearchOperators';
import { DownloadOption } from 'src/lib/makeApiRequest';
import { normalizeForForm } from 'src/lib/QueryBuilderHelper';
import { Filter, Filters } from 'src/models/AdvanceSearchModel';
import {
  DELIVERY_TYPE,
  DownloadOptionsModel,
  FORMAT,
  ORIENTATION,
} from 'src/models/DownloadModel';
import {
  DonorDashboardData,
  DonorDashboardTotals,
  DonorOrganisationResult,
  DonorOrganisationStatus,
  DonorSection,
} from 'src/models/DonorDashboardModel';
import { PAYMENT_STATUS } from 'src/models/PaymentModel';
import { ENTITIES, QuerybuilderModel } from 'src/models/QuerybuilderModel';
import { UserOrganisationItemModel } from 'src/models/UserOrganisationListModel';

const RECORD_PAGE_SIZE = 1000;

const UNPAID_PLEDGE_STATUSES = [
  INVOICE_PAYMENT_STATUS.not_paid,
  INVOICE_PAYMENT_STATUS.partially_paid,
  INVOICE_PAYMENT_STATUS.scheduled_due,
];

export const getYearRange = (year: number): [Moment, Moment] => [
  moment({ year, month: 0, day: 1 }).startOf('day'),
  moment({ year, month: 11, day: 31 }).endOf('day'),
];

const getPaymentFilters = (
  year: number,
  paymentStatus: PAYMENT_STATUS,
  dateField: string,
): Filters => {
  const [start, end] = getYearRange(year);
  return {
    and: [
      ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, paymentStatus],
      [dateField, SEARCH_OPERATORS_PRIMITIVE.gte, start],
      [dateField, SEARCH_OPERATORS_PRIMITIVE.lte, end],
    ],
  };
};

export const buildPaidPaymentsQuery = (year: number): QuerybuilderModel => ({
  filters: getPaymentFilters(year, PAYMENT_STATUS.paid, 'payments.paid_date'),
  pagination: { page: 1, size: RECORD_PAGE_SIZE },
});

export const buildScheduledPaymentsQuery = (year: number): QuerybuilderModel => ({
  filters: getPaymentFilters(year, PAYMENT_STATUS.scheduled, 'payments.scheduled_date'),
  pagination: { page: 1, size: RECORD_PAGE_SIZE },
});

export const buildInvoicesQuery = (year: number): QuerybuilderModel => {
  const [start, end] = getYearRange(year);
  return {
    filters: {
      and: [
        ['invoices.issued_date', SEARCH_OPERATORS_PRIMITIVE.gte, start],
        ['invoices.issued_date', SEARCH_OPERATORS_PRIMITIVE.lte, end],
        [
          'invoices.state',
          SEARCH_OPERATORS_PRIMITIVE.in,
          [INVOICE_STATUS.final, INVOICE_STATUS.legacy, INVOICE_STATUS.error],
        ],
      ],
    },
    pagination: { page: 1, size: RECORD_PAGE_SIZE },
  };
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getRows = (
  response: APIResponse | null,
  entity: ENTITIES,
): Array<Record<string, any>> => {
  if (!response?.success) {
    return [];
  }
  return (response.data?.result ?? [])
    .map((row: Record<string, any>) => normalizeForForm(row, entity));
};

const getCount = (response: APIResponse | null, rows: Array<Record<string, any>>): number => (
  response?.success ? toNumber(response.data?.count) || rows.length : 0
);

const safeEntitiesRequest = async (
  organisationId: string,
  entity: ENTITIES,
  query: QuerybuilderModel,
): Promise<APIResponse | null> => {
  try {
    return await getEntitiesData(organisationId, entity, query);
  } catch {
    return null;
  }
};

const getOrganisationStatus = (
  failedSections: DonorSection[],
  paidResponse: APIResponse | null,
  scheduledResponse: APIResponse | null,
  invoiceResponse: APIResponse | null,
): DonorOrganisationStatus => {
  if (failedSections.length === 0) {
    return 'success';
  }
  const allFailed = !paidResponse?.success
    && !scheduledResponse?.success
    && !invoiceResponse?.success;
  return allFailed ? 'error' : 'partial';
};

export const fetchDonorOrganisationData = async (
  organisation: UserOrganisationItemModel,
  year: number,
): Promise<DonorOrganisationResult> => {
  const [paidResponse, scheduledResponse, invoiceResponse] = await Promise.all([
    safeEntitiesRequest(
      organisation.id,
      ENTITIES.payments,
      buildPaidPaymentsQuery(year),
    ),
    safeEntitiesRequest(
      organisation.id,
      ENTITIES.payments,
      buildScheduledPaymentsQuery(year),
    ),
    safeEntitiesRequest(
      organisation.id,
      ENTITIES.invoices,
      buildInvoicesQuery(year),
    ),
  ]);

  const paidRows = getRows(paidResponse, ENTITIES.payments);
  const scheduledRows = getRows(scheduledResponse, ENTITIES.payments);
  const invoiceRows = getRows(invoiceResponse, ENTITIES.invoices);

  const paidTotal = paidRows.reduce((sum, payment) => sum + toNumber(payment.amount), 0);
  const deductibleTotal = paidRows.reduce(
    (sum, payment) => sum + toNumber(payment.deductible),
    0,
  );
  const scheduledTotal = scheduledRows.reduce(
    (sum, payment) => sum + toNumber(payment.amount),
    0,
  );
  const unpaidRows = invoiceRows.filter(
    (invoice) => UNPAID_PLEDGE_STATUSES.includes(invoice.payment_status),
  );
  const unpaidPledgesTotal = unpaidRows.reduce(
    (sum, invoice) => sum + toNumber(invoice.balance),
    0,
  );

  const failedSections: DonorSection[] = [];
  if (!paidResponse?.success) {
    failedSections.push('paid');
  }
  if (!scheduledResponse?.success) {
    failedSections.push('scheduled');
  }
  if (!invoiceResponse?.success) {
    failedSections.push('unpaid');
  }

  return {
    id: organisation.id,
    nameEn: organisation.name_en,
    nameFr: organisation.name_fr,
    status: getOrganisationStatus(
      failedSections,
      paidResponse,
      scheduledResponse,
      invoiceResponse,
    ),
    failedSections,
    metrics: {
      paidTotal,
      paidCount: getCount(paidResponse, paidRows),
      deductibleTotal,
      scheduledTotal,
      unpaidPledgesTotal,
      unpaidPledgesCount: unpaidRows.length,
      invoiceCount: getCount(invoiceResponse, invoiceRows),
    },
  };
};

const emptyTotals = (): DonorDashboardTotals => ({
  paidTotal: 0,
  paidCount: 0,
  deductibleTotal: 0,
  organisationsIncluded: 0,
  organisationsFailed: 0,
});

export const aggregateDonorOrganisationResults = (
  organisations: DonorOrganisationResult[],
): DonorDashboardData => {
  const totals = organisations.reduce((accumulator, organisation) => {
    if (!organisation.failedSections.includes('paid')) {
      accumulator.paidTotal += organisation.metrics.paidTotal;
      accumulator.paidCount += organisation.metrics.paidCount;
      accumulator.deductibleTotal += organisation.metrics.deductibleTotal;
      accumulator.organisationsIncluded += 1;
    }
    if (organisation.status !== 'success') {
      accumulator.organisationsFailed += 1;
    }
    return accumulator;
  }, emptyTotals());

  return {
    organisations,
    totals,
    hasPartialFailures: totals.organisationsFailed > 0,
  };
};

export const fetchDonorDashboardData = async (
  organisations: UserOrganisationItemModel[],
  year: number,
): Promise<DonorDashboardData> => {
  const results = await Promise.all(
    organisations.map((organisation) => fetchDonorOrganisationData(organisation, year)),
  );
  return aggregateDonorOrganisationResults(results);
};

const buildTaxReceiptFilters = (year: number): Filters => {
  const [start, end] = getYearRange(year);
  return {
    and: [
      ['payments.paid_date', SEARCH_OPERATORS_PRIMITIVE.gte, start],
      ['payments.paid_date', SEARCH_OPERATORS_PRIMITIVE.lte, end],
      ['tax_receipts.voided', SEARCH_OPERATORS_PRIMITIVE.null, ''],
    ] as Filter[],
  };
};

export interface TaxReceiptCount {
  ok: boolean;
  count: number;
}

export const countDonorTaxReceipts = async (
  organisationId: string,
  year: number,
): Promise<TaxReceiptCount> => {
  const response = await getEntitiesData(organisationId, ENTITIES.taxReceipts, {
    filters: buildTaxReceiptFilters(year),
    pagination: { page: 1, size: 1 },
  });
  return {
    ok: response.success,
    count: toNumber(response.data?.count),
  };
};

export const downloadDonorTaxReceipts = (
  organisationId: string,
  year: number,
): Promise<APIResponse> => {
  const options: DownloadOptionsModel = {
    file: `tax-receipts-${organisationId.replace(/[^a-z0-9_-]/gi, '_')}-${year}.pdf`,
    orientation: ORIENTATION.portrait,
    format: FORMAT.pdf,
    delivery: DELIVERY_TYPE.download,
    payment_receipt: false,
  };
  return postDownloadEntitesData(
    organisationId,
    ENTITIES.taxReceipts,
    { filters: buildTaxReceiptFilters(year), options },
    options,
    DownloadOption.download,
  );
};
